/**
 * Guide Overview — the entry point. Frames Vision Intelligence as a site-level
 * SKU decision surface built from four analysis screens over one shared
 * snapshot: SKU velocity, Capacity scenarios, Location review, Non-movers.
 * Carries the fictive-data disclaimer as a left-accent callout, and lays out
 * what's live today against what's on the horizon. Claims nothing beyond what
 * the app actually does — the conversational agent and pinnable dashboard are
 * being adapted and sit on the roadmap, not in "live today".
 */

import { CircleDot, Sparkles } from "lucide-react";

import { type GuideBulletItem, GuideBulletList, GuidePage } from "@/features/guide/guide-page";

const LIVE: GuideBulletItem[] = [
  {
    text: "SKU velocity — every SKU held at the UKDC on one field, by velocity against the money or space it consumes, with four lenses that re-read any cohort you select",
  },
  {
    text: "Capacity scenarios — held SKUs against the site's pick-face ceiling through 2029, with levers to free or buy space and NPI intake to phase",
  },
  {
    text: "Location review — the SKUs whose velocity and depth call for a lane or automation, ranked and exportable for the WMS team",
  },
  {
    text: "Non-movers — SKUs that haven't shipped yet still hold a pick face, ready to relocate, de-list, or feed into a capacity scenario",
  },
  {
    text: "Read-only — every screen reads the shared snapshot and exports decisions; none write back",
  },
];

const HORIZON: GuideBulletItem[] = [
  {
    text: "A conversational agent and a pinnable dashboard — both being adapted, returning to the menu once ready",
  },
  { text: "Extending beyond the UKDC pilot to other J&J Vision distribution sites" },
  { text: "Moving from fictive data to governed, real data" },
  { text: "Writing accepted slotting and relocation decisions back into planning systems" },
];

export function GuideOverviewPage() {
  return (
    <GuidePage
      eyebrow="Application guide"
      title="A walkthrough of Vision Intelligence."
      intro="Vision Intelligence is a site-level SKU decision surface for the UKDC pilot. Four screens, built over one shared snapshot, take you from the whole portfolio to an actionable list: SKU velocity plots every SKU by how fast it moves against the money or space it consumes; Capacity scenarios tests the held total against the site's pick-face ceiling through 2029; and Location review and Non-movers turn that into slotting and relocation lists you can accept, remove, and export. This guide explains how the screens fit together and what each one is for."
      cta={{ to: "/guide/how-it-works", label: "See how it fits together" }}
    >
      {/* Fictive-data disclaimer — rendered as a left-accent callout. */}
      <section className="border-muted bg-warm-gray-02 rounded-lg border-l-4 p-4">
        <p className="text-foreground text-sm font-medium">
          Fictive data for the sake of demonstrating functionality.
        </p>
        <p className="text-muted mt-1 text-xs leading-5">
          Every dataset in this demo is synthetic, built to show what the surface can do with
          current technology. Nothing here reflects a real Johnson &amp; Johnson SKU, shipment, or
          inventory position.
        </p>
      </section>

      <section className="border-border bg-card rounded-lg border shadow-sm">
        <div className="divide-border grid grid-cols-1 divide-y lg:grid-cols-2 lg:divide-x lg:divide-y-0">
          <div className="p-6">
            <div className="flex items-center gap-2">
              <Sparkles className="text-muted h-4 w-4" aria-hidden />
              <h2 className="text-foreground text-sm font-semibold">What&rsquo;s live today</h2>
            </div>
            <p className="text-muted mt-1 text-xs">Wired up end-to-end and ready to demo.</p>
            <GuideBulletList items={LIVE} className="mt-4" />
          </div>
          <div className="p-6">
            <div className="flex items-center gap-2">
              <CircleDot className="text-warm-gray-06 h-4 w-4" aria-hidden />
              <h2 className="text-foreground text-sm font-semibold">On the horizon</h2>
            </div>
            <p className="text-muted mt-1 text-xs">Planned for the next iterations.</p>
            <GuideBulletList
              items={HORIZON.map((b) => ({ ...b, tone: "muted" as const }))}
              className="mt-4"
            />
          </div>
        </div>
      </section>
    </GuidePage>
  );
}
